import { Search, Command, Loader2 } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { Box, Flex, Text } from "@radix-ui/themes";
import { searchService } from "@/services/breeze/SearchService";
import debounce from "lodash/debounce";
import { toast } from "react-hot-toast";
import { SearchResult } from "@/types/breeze/Search";
import { useAuth } from "@/providers/AuthProvider";

// Custom CSS for theme-aware GlobalSearch styling
const globalSearchStyles = `
  .global-search-trigger {
    display: flex;
    align-items: center;
    width: 384px;
    gap: 8px;
    padding: 6px 12px;
    font-size: 14px;
    color: var(--gray-11);
    background: var(--color-panel-solid);
    border: 1px solid var(--gray-6);
    border-radius: var(--radius-3);
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;
  }

  .global-search-trigger:hover {
    background: var(--gray-3);
  }

  .global-search-trigger:focus {
    outline: none;
    border-color: var(--accent-8);
    box-shadow: 0 0 0 1px var(--accent-8);
  }

  .global-search-overlay {
    position: fixed;
    inset: 0;
    z-index: 50;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 80px;
  }

  .global-search-modal {
    background: var(--color-panel-solid);
    width: 100%;
    max-width: 672px;
    border-radius: var(--radius-4);
    box-shadow: var(--shadow-6);
    border: 1px solid var(--gray-6);
  }

  .global-search-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    border-bottom: 1px solid var(--gray-6);
  }

  .global-search-input {
    flex: 1;
    font-size: 14px;
    background: transparent;
    outline: none;
    color: var(--gray-12);
    border: none;
  }

  .global-search-input::placeholder {
    color: var(--gray-11);
  }

  .global-search-results {
    padding: 8px;
    max-height: 60vh;
    overflow-y: auto;
  }

  .global-search-result-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 8px 12px;
    font-size: 14px;
    color: var(--gray-12);
    background: transparent;
    border: none;
    border-radius: var(--radius-3);
    cursor: pointer;
    transition: background-color 0.2s ease;
    text-align: left;
  }

  .global-search-result-item:hover {
    background: var(--gray-3);
  }

  .global-search-footer {
    padding: 16px;
    border-top: 1px solid var(--gray-6);
    text-align: center;
  }

  .global-search-kbd {
    padding: 4px 8px;
    font-size: 12px;
    color: var(--gray-11);
    background: var(--gray-3);
    border-radius: var(--radius-2);
    border: 1px solid var(--gray-6);
  }

  .global-search-shortcut {
    display: flex;
    align-items: center;
    margin-left: auto;
    gap: 4px;
  }
`;

export function GlobalSearch() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { safeNavigate } = useAuth();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen((open) => !open);
      }

      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".global-search-modal")) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (term: string) => {
      if (!term.trim()) {
        setResults([]);
        setIsLoading(false);
        return;
      }

      try {
        const response = await searchService.search(term);
        setResults(response.items);
      } catch (error) {
        console.error("Search error:", error);
        toast.error("Failed to perform search. Please try again.");
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    [],
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsLoading(true);
    debouncedSearch(value);
  };

  const handleResultClick = (result: SearchResult) => {
    if (result.__url) {
      const transformedUrl = result.__url.replace("/items/", "/");
      safeNavigate(transformedUrl);
      setIsOpen(false);
    }
  };

  const getTitle = (result: SearchResult) => {
    return result["__name"]?.toString() ?? `Item ${result.id}`;
  };

  return (
    <>
      {/* Inject custom styles */}
      <style dangerouslySetInnerHTML={{ __html: globalSearchStyles }} />

      <button onClick={() => setIsOpen(true)} className="global-search-trigger">
        <Search size={16} />
        <Text size="2">Search anything...</Text>
        <div className="global-search-shortcut">
          <Command size={12} />
          <Text size="1">K</Text>
        </div>
      </button>

      {isOpen && (
        <div
          className="global-search-overlay"
          data-testid="global-search-overlay"
        >
          <div className="global-search-modal">
            <div className="global-search-header">
              <Search size={20} style={{ color: "var(--gray-11)" }} />
              <input
                type="text"
                placeholder="Search for anything..."
                className="global-search-input"
                value={searchTerm}
                onChange={handleSearch}
                autoFocus
              />
              {isLoading && (
                <Loader2
                  size={16}
                  style={{
                    color: "var(--gray-11)",
                    animation: "spin 1s linear infinite",
                  }}
                />
              )}
              <kbd className="global-search-kbd">ESC</kbd>
            </div>

            <div className="global-search-results">
              {results.length > 0 ? (
                <Flex direction="column" gap="1">
                  {results.map((result) => (
                    <button
                      key={result.id}
                      onClick={() => handleResultClick(result)}
                      className="global-search-result-item"
                    >
                      <Search size={16} style={{ color: "var(--gray-11)" }} />
                      <Flex direction="column" flexGrow="1" align="start">
                        <Text size="2" weight="medium">
                          {getTitle(result)}
                        </Text>
                      </Flex>
                      <Text
                        size="1"
                        style={{
                          color: "var(--gray-11)",
                          textTransform: "capitalize",
                        }}
                      >
                        {result.__entity}
                      </Text>
                    </button>
                  ))}
                </Flex>
              ) : searchTerm && !isLoading ? (
                <Box p="3">
                  <Text size="2" color="gray">
                    No results found
                  </Text>
                </Box>
              ) : null}
            </div>

            <div className="global-search-footer">
              <Text size="1" color="gray">
                Press ESC to close, enter to select
              </Text>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
